import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import PaymentContext from "./PaymentContext";
import useUser from "../../hooks/user/useUser";
// import { sendEmail } from "../../data/api";
import useCountry from "../../hooks/config/useConfig";

type Props = { children: ReactNode };

const PAYMENT_STATUS_KEY = "payment_status_v1";

type CachedStatus = {
  irisId: string;
  isPaid: boolean | null;
  icId: number | null;
  ts: number;
};

export const PaymentProvider = ({ children }: Props) => {
  const { userState, accessToken, isLoggedIn } = useUser();
  const { countryFeatures } = useCountry();
  const [isPaid, setIsPaid] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState<boolean>(false);
  const [icId, setIcId] = useState<number | null>(null);
  const [countryId, setCountryId] = useState<number | null>(null);

  const getCached = (): CachedStatus | null => {
    try {
      const raw = window.localStorage.getItem(PAYMENT_STATUS_KEY);
      if (!raw) return null;
      return JSON.parse(raw) as CachedStatus;
    } catch {
      return null;
    }
  };

  const setCached = (value: CachedStatus) => {
    try {
      window.localStorage.setItem(PAYMENT_STATUS_KEY, JSON.stringify(value));
    } catch { }
  };

  const clearCached = () => {
    try {
      window.localStorage.removeItem(PAYMENT_STATUS_KEY);
    } catch { }
  };

  const refreshPaymentStatus = useCallback(async () => {
    if (!isLoggedIn || !userState?.id || !accessToken) {
      setIsPaid(null);
      setIcId(null);
      clearCached();
      return;
    }

    setIsChecking(true);
    try {
      const res = await fetch(`https://iris-auth.infocasas.com.uy/api/payment/get-status`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${accessToken}`
        },
        body: JSON.stringify({ iris_id: Number(userState.id) })
      });

      if (!res.ok) {
        setIsPaid(false);
        setIcId(null);
        setCached({ irisId: userState.id, isPaid: false, icId: null, ts: Date.now() });
        return;
      }

      const data = await res.json();
      // Debug: log raw response payload for testing purposes
      try { console.log('[Payment] get-status response:', data); } catch { }
      const paidRaw: any = data?.data?.paid;
      const paid: boolean | null = (paidRaw === null || paidRaw === undefined) ? null : !!paidRaw;
      const receivedIcId: number | null = data?.data?.ic_id ?? null;
      // Derive country from logged-in user, not from endpoint
      const userCountryId: number | null = (() => {
        const fromUserCountry: any = (userState as any)?.country?.id;
        const fromUserCountryID: any = (userState as any)?.countryID;
        const parsedFromCountry = fromUserCountry != null ? Number(fromUserCountry) : null;
        const parsedFromCountryID = fromUserCountryID != null ? Number(fromUserCountryID) : null;
        return (parsedFromCountry && !Number.isNaN(parsedFromCountry)) ? parsedFromCountry
          : (parsedFromCountryID && !Number.isNaN(parsedFromCountryID)) ? parsedFromCountryID
            : null;
      })();
      try { console.log('[Payment] user countryId:', userCountryId, 'from userState:', (userState as any)?.country, (userState as any)?.countryID); } catch { }
      setIsPaid(paid);
      setIcId(receivedIcId);
      setCountryId(userCountryId);
      setCached({ irisId: userState.id, isPaid: paid, icId: receivedIcId, ts: Date.now() });

      // If user is paid but still without ic_id, open WhatsApp chat with default message
      // Note: Chile (7) is excluded because they never have ic_id (no tieneTalonIris system)

      {/*

      if (paid === true && (userCountryId === 1 || userCountryId === 5) && receivedIcId === null) {
        try {
          // Country-specific WhatsApp numbers
          const waLink = userCountryId === 5
            ? `https://wa.me/573173637660`
            : (countryFeatures?.contact_wame || '');
          if (waLink && typeof window !== 'undefined') {
            const defaultMsg = `Hola, quiero activar Iris. Mis datos: nombre=${userState.name || ''}, email=${userState.email || ''}, teléfono=${userState.phone || ''}.`;
            const url = waLink.includes('text=')
              ? `${waLink}%20${encodeURIComponent(defaultMsg)}`
              : `${waLink}?text=${encodeURIComponent(defaultMsg)}`;
            window.open(url, '_blank');
          }
        } catch (e) { }
      }
      */}

    } catch (e) {
      setIsPaid(false);
      setIcId(null);
      setCached({ irisId: userState.id, isPaid: false, icId: null, ts: Date.now() });
    } finally {
      setIsChecking(false);
    }
  }, [isLoggedIn, userState?.id, accessToken]);

  useEffect(() => {
    if (!isLoggedIn) {
      setIsPaid(null);
      clearCached();
      return;
    }

    const cached = getCached();
    if (cached && cached.irisId === userState.id) {
      setIsPaid(cached.isPaid);
      setIcId(cached.icId ?? null);
    }

    refreshPaymentStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, userState?.id]);

  const value = useMemo(() => ({
    isPaid,
    icId,
    countryId,
    isChecking,
    lastCheckedAt: getCached()?.ts,
    refreshPaymentStatus
  }), [isPaid, icId, countryId, isChecking, refreshPaymentStatus]);

  return (
    <PaymentContext.Provider value={value}>
      {children}
    </PaymentContext.Provider>
  );
};

export default PaymentProvider;


